/**
 * The words on the MCP connect screen (`hub-mcp-connect-page.tsx`).
 *
 * ★ KEPT SEPARATE BECAUSE THIS SCREEN IS A CONTROL, NOT DECORATION. The connection runs on the
 * signed-in user's own permission context — there is no role picker, by design — so this screen is
 * the only place a customer learns what they are about to hand an AI client. If the words are
 * vague, the customer's consent is uninformed and nothing later corrects it.
 *
 * Draft copy, 2026-09-21, for Herb to review during dev testing.
 *
 * Tone rules followed here, worth keeping if the words change:
 *
 * - Say what it CAN do, not what it MIGHT do. "can read and change" beats "may access data".
 * - Name the user, because the permissions are theirs and that is the whole point.
 * - Never call the AI client trustworthy. We did not write it and cannot vouch for it; the most we
 *   should say is its own declared name, as text.
 * - No reassurance we cannot back. "Secure connection" means nothing to someone deciding this.
 * - Tell them how to undo it before they do it, not after.
 */

export const mcpConnectCopy = {
    /** Shown while the page works out who is signed in. */
    checking: 'Checking your account…',

    title: 'Connect an AI assistant',

    /** `{client}` is the AI client's self-declared name. Rendered as text, never as markup. */
    intro: (client: string) => `${client} is asking to connect to your HDPhotoHub account.`,

    /**
     * The heart of the screen. Deliberately concrete about scope, and deliberately does not promise
     * that the assistant will behave — it promises what the connection makes possible.
     */
    whatItCanDo: {
        heading: 'What this will let it do',
        body: (userName: string) =>
            `It will act as you — ${userName} — and can read and change the same things you can in HDPhotoHub, including orders, sites, media, clients and team members. It can make changes without asking you first.`,
        /**
         * Used instead of `body` when the deployment offers a scope checklist (MCP-SERVER-PLAN.md
         * §2.3b, S11): the list of areas is then the checklist itself, not a sentence.
         */
        bodyScoped: (userName: string) =>
            `It will act as you — ${userName} — and can read and change the same things you can in HDPhotoHub, but only in the areas ticked below. It can make changes without asking you first.`,
        narrower:
            'If you want a narrower connection, sign in as a team member with more limited access and connect from that account instead.'
    },

    /**
     * The scope checklist. Shown only when the MCP deployment offers scopes; the names and labels
     * come from that deployment, so nothing here lists them.
     *
     * ★ THE COUNT OF CHANGING TOOLS IS ON EVERY ROW ON PURPOSE. Nothing asks before the assistant
     * makes a change, so the number that can is the most useful thing on the row — it is what
     * separates ticking "Tags" from ticking "Order Form".
     */
    scopes: {
        heading: 'Areas it can work in',
        help: 'Untick anything this assistant does not need. You can only change this by connecting again.',
        detail: (tools: number, writes: number) =>
            writes === 0
                ? `${tools} ${tools === 1 ? 'tool' : 'tools'}, read only`
                : `${tools} ${tools === 1 ? 'tool' : 'tools'}, ${writes} can make changes`,
        loading: 'Checking what this connection can offer…',
        none: 'Tick at least one area to connect.'
    },

    /** Where consent stops being a one-way door. */
    control: {
        heading: 'You stay in control',
        items: [
            'A new API token is created for this connection, and only this connection uses it.',
            'You can see it any time under Business → API & Webhooks.',
            'Deleting it there disconnects the assistant immediately.',
            'Every action it takes is recorded against this connection.'
        ]
    },

    /**
     * Who is about to be connected, at the top of the screen (Herb, 2026-10-02).
     *
     * ★ THE CONNECTION RUNS AS THIS USER, so the account is the first thing on the screen and not a
     * detail inside a sentence. Someone with two logins — an owner and a team member, or an admin
     * on Login As — must be able to see which one this is and change it without abandoning the
     * connection request.
     */
    account: {
        signedInAs: 'Connecting as',
        /** Login As: the session belongs to the user above, started by this admin. */
        via: (superUserName: string) => `Signed in by ${superUserName} using Login As`,
        switch: 'Not you? Sign out',
        switchHelp: 'You will be asked to sign in again, then come straight back here.'
    },

    /** Shown above the buttons, so the last thing read before approving is the caution. */
    beforeYouApprove:
        'Only connect assistants you recognise and intend to use. Anyone who can use this assistant can use your HDPhotoHub account through it.',

    approve: 'Connect',
    deny: 'Cancel',

    /** Herb, 2026-09-21: Admin, Business Owner and Team Member only. */
    notPermitted: {
        heading: 'Not available for this account',
        body: 'AI assistant connections are available to business owners, team members and administrators. Your account type cannot connect one.'
    },

    errors: {
        expired:
            'This connection request has expired. Start again from your AI assistant and finish within ten minutes.',
        tokenFailed:
            'HDPhotoHub could not create the connection token. Nothing has been connected — try again, and if it keeps failing, contact support.',
        handoffFailed:
            'The connection could not be completed. Nothing has been connected. Start again from your AI assistant.',
        missingHandoff:
            'This page is only used while connecting an AI assistant. Start from your assistant to connect one.'
    }
} as const;
